import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { ContentBlock } from "@/components/wpl/content-block/src/content-block";
import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework/src/localization/use-localized-href";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { TryPageCard } from "@/templates/try/try-page/components/try-page-card";

const sectionCss = css({
    color: wacToken("color.neutral.light0"),
    backgroundColor: wacToken("color.extended.blue800"),
    padding: "4rem 0",
    position: "relative",
    verticalAlign: "baseline",
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%"
});
const headerStyle = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2": { fontWeight: "500", fontSize: "2.25rem" },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".contentBlock-body": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        p: {
            fontWeight: "normal"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        fontWeight: "500"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".ctaButton": {
        padding: "0 0.625rem",
        lineHeight: "1.3"
    }
});
const lowerCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".ctaButton": {
        padding: "0 0.625rem",
        lineHeight: "1.3",
        fontWeight: "500"
    },
    "fontWeight": "normal",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h3": {
        marginBlockEnd: "1rem",
        fontSize: "1.5rem",
        fontWeight: "normal"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        fontWeight: "lighter"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        fontSize: ".875rem",
        color: wacToken("color.extended.blue800")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "img": { height: "140px" }
});
const imageCss = css({
    padding: "16px 0",
    justifyContent: "center",
    alignItems: "center",
    display: "flex"
});

const EnterpriseSection = () => {
    const intl = useIntl();
    const goBigHeader = intl.formatMessage({
        id: "try.enterprise.goBig",
        defaultMessage: "Go big with Atlassian Enterprise"
    });
    const goBigBody = intl.formatMessage({
        id: "try.enterprise.goBigBody",
        defaultMessage:
            "Connect, accelerate, and scale your entire enterprise with Atlassian enterprise solutions."
    });
    const learnMore = intl.formatMessage({
        id: "try.enterprise.learnMore",
        defaultMessage: "Learn more"
    });

    const cloud = intl.formatMessage({
        id: "try.enterprise.cloud",
        defaultMessage: "Cloud"
    });
    const cloudBody = intl.formatMessage({
        id: "try.enterprise.cloudBody",
        defaultMessage:
            "Atlassian cloud solutions are equipped with everything large and growing teams need to scale collaboration and workflows seamlessly."
    });

    const dataCenter = intl.formatMessage({
        id: "try.enterprise.dataCenter",
        defaultMessage: "Data Center"
    });
    const dataCenterBody = intl.formatMessage({
        id: "try.enterprise.dataCenterBody",
        defaultMessage:
            "Host our applications with IaaS vendors like AWS or Azure. Data Center is our self-managed solution for advanced administration at scale."
    });

    return (
        <section css={sectionCss}>
            <div css={headerStyle}>
                <Row>
                    <ContentBlock
                        headerText={goBigHeader}
                        headerType={"h2"}
                        isCentered
                        bodyText={goBigBody}
                        ctaProps={{
                            buttonProps: {
                                className: "ctaButton",
                                text: learnMore,
                                url: "https://www.atlassian.com/enterprise",
                                type: "primary",
                                color: "lightblue",
                                size: "small"
                            }
                        }}
                    />
                </Row>
            </div>
            <div css={lowerCss}>
                <Row>
                    <TryPageCard
                        title={cloud}
                        link={useLocalizedHref("/enterprise/cloud")}
                        bodyText={cloudBody}
                        ctaText={learnMore}
                        buttonColor={"lightblue"}
                        buttonType={"primary"}
                    />
                    <TryPageCard
                        title={dataCenter}
                        link={useLocalizedHref("/enterprise/data-center")}
                        bodyText={dataCenterBody}
                        ctaText={learnMore}
                        buttonColor={"lightblue"}
                        buttonType={"primary"}
                    />
                    <div css={imageCss}>
                        <Picture
                            image={{
                                src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/database-server-on-v3.webp",
                                alt: intl.formatMessage({
                                    id: "try.enterprise.databaseServer",
                                    defaultMessage: "Data center + server"
                                })
                            }}
                        />
                    </div>
                </Row>
            </div>
        </section>
    );
};

export default EnterpriseSection;
